import {
  HowYouPlayDiagram as Diagram,
  moduleFileName,
} from "@softmax/product-ux/catalog";
import type {
  StoreDiagramBoard,
  StoreDiagramFeatured,
  StorePlayerBuildSample,
} from "@softmax/product-ux/catalog";
import { formatRelative } from "../../play/state";
import type { PreviewContent } from "../content/preview-content.types";
import { GeneralsBoard } from "../board/generals-board";
type PlayerBuildSample = Omit<StorePlayerBuildSample, "filename"> & {
  module: string;
};
const playerCode: Record<string, PlayerBuildSample> = {
  "paintbot-royal": {
    instruction: "make it hold the center and trade late",
    module: "player",
    code: [
      { text: "def decide(obs):", depth: 0 },
      { text: "if obs.zone.near:", depth: 1 },
      { text: "return hold()", depth: 2 },
    ],
  },
  "generals-competition": {
    instruction: "expand early, push the general late",
    module: "player",
    code: [
      { text: "def decide(obs):", depth: 0 },
      { text: "if obs.turn < 50:", depth: 1 },
      { text: "return expand()", depth: 2 },
    ],
  },
};
export function HowYouPlayDiagram({
  content,
  fig = "b",
}: {
  content: PreviewContent;
  fig?: "a" | "b" | undefined;
}) {
  const sample = playerCode[content.slug] ?? {
    instruction: "be bolder in the opening",
    module: "player",
    code: [
      { text: "def decide(obs):", depth: 0 as const },
      { text: `# ${content.name}`, depth: 1 as const },
      { text: "return act(obs)", depth: 1 as const },
    ],
  };
  const top = content.standings[0];
  const featured = featuredPlayer(content);
  const item = content.media.find(
    (media) => media.kind === "board" || media.kind === "capture",
  );
  let board: StoreDiagramBoard = { kind: "empty" };

  if (item?.kind === "board") {
    board = { kind: "board", content: <GeneralsBoard size={460} /> };
  } else if (item) {
    board = { kind: "capture", src: item.poster ?? item.src, alt: item.alt };
  } else {
    const still =
      content.media.find((media) => media.poster) ??
      content.media.find((media) => media.kind !== "replay" && media.src);

    if (still)
      board = {
        kind: "capture",
        src: still.poster ?? still.src,
        alt: still.alt,
      };
    else if (content.art)
      board = { kind: "capture", src: content.art, alt: content.name };
  }

  return (
    <Diagram
      sample={{
        instruction: sample.instruction,
        filename: moduleFileName(top?.name ?? sample.module),
        code: sample.code,
      }}
      featured={featured}
      board={board}
      cadence={content.copy.cadenceShort}
      scoreLabel={content.scoreLabel ?? "Score"}
      fig={fig}
    />
  );
}

function featuredPlayer(content: PreviewContent): StoreDiagramFeatured | null {
  const top = content.standings[0];
  let featured: StoreDiagramFeatured | null = null;

  if (top) {
    const rank = Number(top.rank);
    featured = {
      name: top.name,
      rank: content.playerCount
        ? `#${rank} of ${content.playerCount}`
        : `#${rank}`,
      score: top.score,
      roundsPlayed: top.roundsPlayed,
      lastRound: content.ladder?.lastRoundAt
        ? formatRelative({
            iso: content.ladder.lastRoundAt,
            now: content.generatedAt,
            timeZone: "UTC",
          })
        : null,
    };
  }

  return featured;
}
